/**
 * Gift Date Picker
 * Initializes flatpickr with custom theme on elements with [data-bt-datepicker]
 *
 * Usage:
 *   <input type="text" data-bt-datepicker>
 *
 * Options (via data attributes):
 *   data-min-days-ahead="1"  - Minimum days from today (default: 1)
 *   data-date-format="d/m/Y" - Display date format (default: d/m/Y)
 *
 * Output:
 *   ISO datetime string at 9am in user's local timezone
 *   e.g., "2026-01-16T09:00:00+00:00"
 */

class BTDatepicker {
  constructor(element) {
    this.element = element;
    this.init();
  }

  init() {
    if (typeof flatpickr === 'undefined') {
      console.warn('BTDatepicker: flatpickr is not loaded');
      return;
    }

    const minDaysAhead = parseInt(this.element.dataset.minDaysAhead) || 1;
    const displayFormat = this.element.dataset.dateFormat || 'd/m/Y';

    // Calculate minimum date
    const minDate = new Date();
    minDate.setDate(minDate.getDate() + minDaysAhead);

    this.picker = flatpickr(this.element, {
      // Store ISO format internally, display friendly format to user
      dateFormat: 'Y-m-d',
      altInput: true,
      altFormat: displayFormat,
      minDate: minDate,
      locale: {
        firstDayOfWeek: 1 // Monday
      },
      disableMobile: true,
      monthSelectorType: 'static',
      prevArrow: '←',
      nextArrow: '→',
      onReady: (selectedDates, dateStr, instance) => {
        instance.calendarContainer.classList.add('gift-datepicker');
        this.addActionButtons(instance);
      },
      onChange: (selectedDates, dateStr, instance) => {
        this.hasInteracted = true;
        if (selectedDates.length > 0) {
          this.element.value = this.toISODateTimeAt9am(selectedDates[0]);
        } else {
          this.element.value = '';
          instance.altInput.value = 'Send now';
        }
        instance.altInput.placeholder = 'Send now';
      }
    });
  }

  /**
   * Convert a date to ISO datetime string at 9am in user's local timezone
   * e.g., "2026-01-16T09:00:00+00:00"
   */
  toISODateTimeAt9am(date) {
    // Create a new date at 9am on the selected day
    const dateAt9am = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 9, 0, 0);

    // Get timezone offset in minutes and convert to hours:minutes format
    const tzOffset = -dateAt9am.getTimezoneOffset();
    const tzHours = Math.floor(Math.abs(tzOffset) / 60);
    const tzMinutes = Math.abs(tzOffset) % 60;
    const tzSign = tzOffset >= 0 ? '+' : '-';
    const tzString = `${tzSign}${String(tzHours).padStart(2, '0')}:${String(tzMinutes).padStart(2, '0')}`;

    // Format as ISO datetime
    const year = dateAt9am.getFullYear();
    const month = String(dateAt9am.getMonth() + 1).padStart(2, '0');
    const day = String(dateAt9am.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}T09:00:00${tzString}`;
  }

  addActionButtons(instance) {
    const container = document.createElement('div');
    container.className = 'flex gap-5 pt-5 pb-1';

    const nowBtn = document.createElement('button');
    nowBtn.type = 'button';
    nowBtn.className = 'btn-primary btn-small flex-1';
    nowBtn.textContent = 'Now';
    nowBtn.addEventListener('click', () => {
      instance.clear();
      instance.close();
    });

    const clearBtn = document.createElement('button');
    clearBtn.type = 'button';
    clearBtn.className = 'btn-primary btn-small flex-1';
    clearBtn.textContent = 'Clear';
    clearBtn.addEventListener('click', () => {
      instance.clear();
      instance.close();
    });

    container.appendChild(nowBtn);
    container.appendChild(clearBtn);
    instance.calendarContainer.appendChild(container);
  }
}

// Initialize all datepickers
function initBTDatepickers() {
  document.querySelectorAll('[data-bt-datepicker]').forEach(el => {
    if (!el._btDatepicker) {
      el._btDatepicker = new BTDatepicker(el);
    }
  });
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initBTDatepickers);
} else {
  initBTDatepickers();
}

// Expose for manual initialization
window.BTDatepicker = BTDatepicker;
window.initBTDatepickers = initBTDatepickers;
